import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { wsRequest } from '../../api/ws'
import type { Host } from '../../store/types'
import { harnessHonorsModelEnv } from '../../util/harness'
import { hostRunsAgents } from '../hosts/hostRoles'
import './NewAgentWizard.css'

// Multi-step builder for a long-lived agent. Flow:
//   name → job description → host → harness → model → review.
// The review step surfaces the working-directory path (derived from the
// JD title and host's agent_home_path) with an inline edit affordance so
// the user can override it before committing. All server mutations
// (create_jd, find_or_create_wd, create_position, create_ai_agent_instance)
// are deferred to the final submit — lets the user back out of any step
// without leaving orphaned rows behind.

type Step = 'name' | 'jd' | 'jd_pick' | 'host' | 'harness' | 'model' | 'submit'

type Harness = { id: number; name: string }
type AIBrainModel = { id: string; name: string; provider?: string }
type JobDescription = { id: string; title: string; mandate?: string }

type NameCollision = {
  kind: 'ai_agent_instance' | 'organization_position' | 'agent_job_description'
  id: string
  name: string
  suggested_rename?: string
  // Populated only for kind === 'agent_job_description'.
  existing_mandate?: string
  active_agent_count?: number
  suggested_new_name?: string
}

export type JDChoice =
  | { mode: 'new'; title: string; mandate: string }
  | { mode: 'existing'; id: string; title: string }

export function NewAgentWizard() {
  const organizationID = useAppStore((s) => s.organizationID)
  const availableOrganizations = useAppStore((s) => s.availableOrganizations)
  // Only hosts that actually run agents are eligible — a display-only box can't
  // (the server rejects it too; this keeps it out of the picker). Blank/legacy
  // roles read as agent, so the existing fleet is unaffected.
  const hosts = useAppStore((s) => s.hosts).filter((h) => hostRunsAgents(h.roles))
  const orgSlug =
    availableOrganizations.find((o) => o.id === organizationID)?.slug ?? null
  const agentInstances = useAppStore((s) => s.agentInstances)
  const popNav = useAppStore((s) => s.popNav)
  const pushNav = useAppStore((s) => s.pushNav)

  const [step, setStep] = useState<Step>('name')
  const [name, setName] = useState('')
  const [namePlaceholder] = useState(randomButlerName)
  const [jdTitlePlaceholder] = useState(randomHouseholdRole)

  // Job description: default to new mode with empty fields.
  const [jdChoice, setJDChoice] = useState<JDChoice>({
    mode: 'new',
    title: '',
    mandate: '',
  })

  // Working-directory path shown on the review step; auto-fills from JD
  // title + host.agent_home_path until the user edits it via the pencil
  // icon on the review screen.
  const [wdPath, setWDPath] = useState('')
  const [pathEdited, setPathEdited] = useState(false)

  const [hostID, setHostID] = useState<string>(() => defaultHostID(hosts))
  const [harnessID, setHarnessID] = useState<number | null>(null)
  const [modelID, setModelID] = useState<string | null>(null)
  // Least-knowledge default (CP1, docs/capability-provisioning.md §6): off means
  // the agent can't enumerate household entities. It can still be granted
  // specific ones later via `hearth acquire`.
  const [allowDiscovery, setAllowDiscovery] = useState(false)
  // CP6: pre-grant resource connections at creation so the agent starts equipped
  // and never has to stop mid-task to `acquire` a foreseeable capability.
  const [connections, setConnections] = useState<
    { id: string; display_name: string }[]
  >([])
  const [grantConnIDs, setGrantConnIDs] = useState<string[]>([])

  // Lazy-loaded lookups.
  const [harnesses, setHarnesses] = useState<Harness[] | null>(null)
  const [availableHarnessIDs, setAvailableHarnessIDs] = useState<Set<number>>(
    () => new Set(),
  )
  const [models, setModels] = useState<AIBrainModel[] | null>(null)
  const [jobDescriptions, setJobDescriptions] = useState<JobDescription[] | null>(null)
  // WD paths in use for the currently-selected host. Lazy-loaded on host
  // change. Used only to warn the user up-front when their path is already
  // taken — the final submit still goes through its own remediation flow.
  const [existingWDPaths, setExistingWDPaths] = useState<Set<string>>(
    () => new Set(),
  )

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collision, setCollision] = useState<NameCollision | null>(null)
  const [jdCollision, setJDCollision] = useState<NameCollision | null>(null)

  useEffect(() => {
    if (!hostID || !organizationID) return
    setHarnesses(null)
    setAvailableHarnessIDs(new Set())
    setHarnessID(null)
    setModelID(null)
    void wsRequest<{
      harnesses?: Harness[]
      available_harness_ids?: number[]
      error?: string
    }>('list_harnesses', { host_id: hostID, organization_id: organizationID }, 'harnesses', {
      kind: 'host',
      id: hostID,
      action: 'list_harnesses',
    })
      .then((r) => {
        setHarnesses(r.harnesses ?? [])
        setAvailableHarnessIDs(new Set(r.available_harness_ids ?? []))
      })
      .catch(() => {
        setHarnesses([])
        setAvailableHarnessIDs(new Set())
      })
  }, [hostID, organizationID])

  useEffect(() => {
    if (!organizationID || harnessID === null) return
    setModels(null)
    setModelID(null)
    void wsRequest<{ ai_brain_models?: AIBrainModel[]; error?: string }>(
      'list_ai_brain_models',
      { organization_id: organizationID, harness_id: harnessID },
      'ai_brain_models',
      { kind: 'organization', id: organizationID, action: 'list_ai_brain_models' },
    )
      .then((r) => setModels(r.ai_brain_models ?? []))
      .catch(() => setModels([]))
  }, [organizationID, harnessID])

  useEffect(() => {
    if (!organizationID || jobDescriptions !== null) return
    void wsRequest<{ agent_job_descriptions?: JobDescription[]; error?: string }>(
      'list_agent_job_descriptions',
      { organization_id: organizationID },
      'agent_job_descriptions',
      {
        kind: 'organization',
        id: organizationID,
        action: 'list_agent_job_descriptions',
      },
    )
      .then((r) => setJobDescriptions(r.agent_job_descriptions ?? []))
      .catch(() => setJobDescriptions([]))
  }, [organizationID, jobDescriptions])

  // CP6: the household's resource connections, for the pre-grant picker on the
  // review step. Empty (no connections, or the fetch failed) → no picker shown.
  useEffect(() => {
    if (!organizationID) return
    void wsRequest<{
      resource_connections?: { id: string; display_name: string }[]
      error?: string
    }>(
      'resource_connections_list',
      { organization_id: organizationID },
      'resource_connections_list',
      {
        kind: 'organization',
        id: organizationID,
        action: 'resource_connections_list',
      },
    )
      .then((r) => setConnections(r.resource_connections ?? []))
      .catch(() => setConnections([]))
  }, [organizationID])

  // Pull WD paths for the chosen host so the review step can warn the
  // user up-front if their path is already in use.
  useEffect(() => {
    if (!organizationID || !hostID) {
      setExistingWDPaths(new Set())
      return
    }
    void wsRequest<{
      working_directories?: { directory_path: string; host_id: string; abandoned_at?: string }[]
      error?: string
    }>(
      'list_working_directories',
      { organization_id: organizationID, host_id: hostID },
      'working_directories',
      {
        kind: 'organization',
        id: organizationID,
        action: 'list_working_directories',
      },
    )
      .then((r) => {
        const live = (r.working_directories ?? []).filter(
          (w) => w.host_id === hostID && !w.abandoned_at,
        )
        setExistingWDPaths(new Set(live.map((w) => w.directory_path)))
      })
      .catch(() => setExistingWDPaths(new Set()))
  }, [organizationID, hostID])

  // wdPath auto-fills from the JD title + the host's agent_home_path,
  // and stops once the user manually edits it via the review screen.
  useEffect(() => {
    if (pathEdited) return
    const host = hosts.find((h) => h.host_id === hostID)
    setWDPath(
      defaultWDPath(jdTitleOf(jdChoice), host?.agent_home_path, orgSlug),
    )
  }, [jdChoice, pathEdited, hostID, hosts, orgSlug])

  function close() {
    popNav()
  }

  // If the org has exactly one host and it's online, the host-picker step
  // is noise — auto-select it and skip forward to harness. Back-nav from
  // harness also skips the host step so the user doesn't land on a
  // single-option picker they never chose to visit.
  const autoHostID =
    hosts.length === 1 && hosts[0].online ? hosts[0].host_id : null

  function advanceFromJD() {
    if (autoHostID) {
      setHostID(autoHostID)
      setStep('harness')
    } else {
      setStep('host')
    }
  }

  function back() {
    switch (step) {
      case 'name':
        close()
        return
      case 'jd':
        setStep('name')
        return
      case 'jd_pick':
        setStep('jd')
        return
      case 'host':
        setStep('jd')
        return
      case 'harness':
        setStep(autoHostID ? 'jd' : 'host')
        return
      case 'model':
        setStep('harness')
        return
      case 'submit':
        // Skip the model step on the way back too when the chosen
        // harness ignores model selection.
        setStep(
          harnessHonorsModelEnv(
            harnesses?.find((h) => h.id === harnessID)?.name,
          )
            ? 'model'
            : 'harness',
        )
        return
    }
  }

  async function submit(
    remediation?: Record<string, unknown>,
    jdOverride?: JDChoice,
  ) {
    if (!organizationID || harnessID === null || !name.trim()) return
    const selectedHarnessName = harnesses?.find((h) => h.id === harnessID)?.name
    if (harnessHonorsModelEnv(selectedHarnessName) && !modelID) return
    setSubmitting(true)
    setError(null)
    // jdOverride lets the JD-collision modal force a specific JD choice
    // (use-existing, update-mandate, rename-new) and resubmit without
    // waiting for setJDChoice's render cycle.
    const effectiveJD = jdOverride ?? jdChoice
    try {
      // Step 1: resolve JD id (create if needed).
      let jdID: string | null = null
      if (effectiveJD.mode === 'existing') {
        jdID = effectiveJD.id
      } else if (effectiveJD.title.trim() !== '') {
        const payload: Record<string, unknown> = {
          organization_id: organizationID,
          title: effectiveJD.title.trim(),
          priority: 5,
        }
        if (effectiveJD.mode === 'new' && effectiveJD.mandate.trim() !== '') {
          payload.mandate = effectiveJD.mandate.trim()
        }
        const resp = await wsRequest<{
          agent_job_description?: { id: string }
          error?: string
          collision?: NameCollision
        }>(
          'create_agent_job_description',
          payload,
          'agent_job_description_created',
          {
            kind: 'organization',
            id: organizationID,
            action: 'create_agent_job_description',
          },
        )
        if (resp.error === 'name_collision' && resp.collision) {
          // If the existing JD already has the same mandate, silently
          // adopt it — the user's intent and the existing record are
          // identical, no remediation needed.
          const existing = (resp.collision.existing_mandate ?? '').trim()
          const attempted =
            effectiveJD.mode === 'new' ? effectiveJD.mandate.trim() : ''
          if (existing === attempted) {
            const next: JDChoice = {
              mode: 'existing',
              id: resp.collision.id,
              title: resp.collision.name,
            }
            setJDChoice(next)
            setSubmitting(false)
            void submit(remediation, next)
            return
          }
          setJDCollision(resp.collision)
          setSubmitting(false)
          return
        }
        if (resp.error || !resp.agent_job_description?.id) {
          setError(resp.error ?? 'failed to create job description')
          setSubmitting(false)
          return
        }
        jdID = resp.agent_job_description.id
      }

      // Step 2: create wd + position. wd is find-or-create on
      // (host_id, directory_path); the position binds it to the JD and
      // inherits its display name from jd.title.
      if (!jdID) {
        setError('a job description is required')
        setSubmitting(false)
        return
      }
      const wdResp = await wsRequest<{
        working_directory?: { id: string }
        error?: string
      }>(
        'find_or_create_working_directory',
        {
          organization_id: organizationID,
          host_id: hostID,
          directory_path: wdPath.trim(),
        },
        'working_directory_found_or_created',
        {
          kind: 'organization',
          id: organizationID,
          action: 'find_or_create_working_directory',
        },
      )
      if (wdResp.error || !wdResp.working_directory?.id) {
        setError(wdResp.error ?? 'failed to create working directory')
        setSubmitting(false)
        return
      }
      const positionPayload: Record<string, unknown> = {
        organization_id: organizationID,
        working_directory_id: wdResp.working_directory.id,
        agent_job_description_id: jdID,
      }
      let posResp = await wsRequest<{
        organization_position?: { id: string }
        error?: string
        collision?: {
          kind: string
          id: string
          name: string
          slot_kind?: string
          slot_id?: string
          live_agent_count?: number
        }
      }>(
        'create_organization_position',
        positionPayload,
        'organization_position_created',
        {
          kind: 'organization',
          id: organizationID,
          action: 'create_organization_position',
        },
      )
      // If the WD is already bound to a position with no live agents, the
      // user's intent is unambiguous: take the slot. Eliminate the empty
      // position and retry. Only surface an error when retiring a live
      // agent would be required.
      if (
        posResp.error === 'slot_collision' &&
        (posResp.collision?.live_agent_count ?? 0) === 0
      ) {
        posResp = await wsRequest<typeof posResp>(
          'create_organization_position',
          { ...positionPayload, remediate_slot_collision: 'remove_conflicting' },
          'organization_position_created',
          {
            kind: 'organization',
            id: organizationID,
            action: 'create_organization_position',
          },
        )
      }
      if (posResp.error === 'slot_collision') {
        const n = posResp.collision?.live_agent_count ?? 0
        setError(
          `That working directory is already in use by another position with ${n} live agent${n === 1 ? '' : 's'}. Retire ${n === 1 ? 'it' : 'them'} or pick a different path.`,
        )
        setSubmitting(false)
        return
      }
      if (posResp.error || !posResp.organization_position?.id) {
        setError(posResp.error ?? 'failed to create position')
        setSubmitting(false)
        return
      }
      const positionID = posResp.organization_position.id

      // Step 3: create the agent instance. Name-collision handled here.
      const payload: Record<string, unknown> = {
        organization_id: organizationID,
        organization_position_id: positionID,
        harness_id: harnessID,
        name: name.trim(),
      }
      if (modelID) payload.ai_brain_model_id = modelID
      if (allowDiscovery) payload.allow_discovery = true
      if (grantConnIDs.length > 0)
        payload.grant_resource_connection_ids = grantConnIDs
      if (remediation) payload.remediate_collision = remediation
      const resp = await wsRequest<{
        ai_agent_instance?: { id: string; organization_position_id: string }
        error?: string
        collision?: NameCollision
        spawn_error?: string
      }>(
        'create_ai_agent_instance',
        payload,
        'ai_agent_instance_created',
        {
          kind: 'organization',
          id: organizationID,
          action: 'create_ai_agent_instance',
        },
        { timeoutMs: 20_000 },
      )
      if (resp.error === 'name_collision' && resp.collision) {
        setCollision(resp.collision)
        setSubmitting(false)
        return
      }
      if (resp.error) {
        setError(resp.error)
        setSubmitting(false)
        return
      }
      const inst = resp.ai_agent_instance
      if (inst?.id && inst.organization_position_id) {
        // Onboarding: kick off the proposer for the new hire, FIRE-AND-FORGET.
        // The suggestions surface via the review-queue banner (LW-2), not inline.
        // We deliberately do NOT await the response for navigation: this call
        // fires right as the agent spawns, and the spawn's concurrent frames on
        // the same connection can race the request's response tag — so the inline
        // hand-off was unreliable. The queue is the robust, unified surface (the
        // Facilitator/LLM path is queue-only anyway), and its banner appears on
        // the home screen the moment the proposal lands.
        void wsRequest(
          'provisioning_propose',
          {
            organization_id: organizationID,
            organization_position_id: inst.organization_position_id,
            ai_agent_instance_id: inst.id,
          },
          'provisioning_proposal_created',
          { kind: 'organization', id: organizationID, action: 'provisioning_propose' },
        ).catch(() => {})
        popNav()
        pushNav({
          kind: 'agent_detail',
          agentInstanceID: inst.id,
          positionID: inst.organization_position_id,
        })
      } else {
        setError('agent created but response was malformed')
        setSubmitting(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitting(false)
    }
  }

  const selectedHost = hosts.find((h) => h.host_id === hostID)
  const selectedHarness = harnesses?.find((h) => h.id === harnessID)
  const selectedModel = models?.find((m) => m.id === modelID)

  // Pre-flight uniqueness sets. The submit-time remediation flow is the
  // authoritative one (handles concurrent races), but warning the user at
  // input time avoids walking through six steps just to bounce off the
  // collision modal at the end.
  const existingAgentNames = new Set(
    agentInstances.filter((a) => a.retired_at === null).map((a) => a.name),
  )
  const existingJDTitles = new Set(
    (jobDescriptions ?? []).map((j) => j.title),
  )

  return (
    <div className="wizard">
      <header className="wizard__nav">
        <button className="wizard__nav-btn" onClick={back}>
          ‹ {step === 'name' ? 'Cancel' : 'Back'}
        </button>
        <div className="wizard__nav-title">{titleFor(step)}</div>
        <div className="wizard__nav-spacer" />
      </header>

      <div className="wizard__body">
        {step === 'name' && (
          <NameStep
            name={name}
            placeholder={namePlaceholder}
            onChange={setName}
            onNext={() => name.trim() && setStep('jd')}
            existingNames={existingAgentNames}
          />
        )}

        {step === 'jd' && (
          <JDStep
            choice={jdChoice}
            onChange={setJDChoice}
            onPickExisting={() => setStep('jd_pick')}
            onNext={advanceFromJD}
            existingTitles={existingJDTitles}
            titlePlaceholder={jdTitlePlaceholder}
          />
        )}

        {step === 'jd_pick' && (
          <JDPickStep
            jobDescriptions={jobDescriptions}
            onPick={(jd) => {
              setJDChoice({ mode: 'existing', id: jd.id, title: jd.title })
              advanceFromJD()
            }}
          />
        )}

        {step === 'host' && (
          <HostStep
            hosts={hosts}
            selectedID={hostID}
            onSelect={(id) => {
              setHostID(id)
              setStep('harness')
            }}
          />
        )}

        {step === 'harness' && (
          <HarnessStep
            harnesses={harnesses}
            availableIDs={availableHarnessIDs}
            hostname={selectedHost?.hostname ?? 'this host'}
            selectedID={harnessID}
            onSelect={(id) => {
              setHarnessID(id)
              const selectedName = harnesses?.find((h) => h.id === id)?.name
              if (harnessHonorsModelEnv(selectedName)) {
                setStep('model')
              } else {
                // Harness configures its model internally — skip the
                // picker and go straight to review with modelID null.
                setModelID(null)
                setStep('submit')
              }
            }}
          />
        )}

        {step === 'model' && (
          <ModelStep
            models={models}
            selectedID={modelID}
            onSelect={(id) => {
              setModelID(id)
              setStep('submit')
            }}
          />
        )}

        {step === 'submit' && (
          <SubmitStep
            agentName={name}
            jd={jdChoice}
            wdPath={wdPath}
            onChangeWDPath={(v) => {
              setPathEdited(true)
              setWDPath(v)
            }}
            hostname={selectedHost?.hostname ?? '—'}
            harnessName={selectedHarness?.name ?? '—'}
            modelName={
              harnessHonorsModelEnv(selectedHarness?.name)
                ? (selectedModel?.name ?? '—')
                : 'harness-managed'
            }
            onSubmit={() => submit()}
            onCancel={close}
            submitting={submitting}
            error={error}
            existingWDPaths={existingWDPaths}
            allowDiscovery={allowDiscovery}
            onToggleDiscovery={setAllowDiscovery}
            connections={connections}
            grantConnIDs={grantConnIDs}
            onToggleConn={(id) =>
              setGrantConnIDs((prev) =>
                prev.includes(id)
                  ? prev.filter((c) => c !== id)
                  : [...prev, id],
              )
            }
          />
        )}
      </div>

      {jdCollision && jdChoice.mode === 'new' && (
        <JDCollisionSheet
          collision={jdCollision}
          attemptedTitle={jdChoice.title.trim()}
          attemptedMandate={jdChoice.mandate.trim()}
          onCancel={() => {
            setJDCollision(null)
            setSubmitting(false)
          }}
          onUseExisting={() => {
            const next: JDChoice = {
              mode: 'existing',
              id: jdCollision.id,
              title: jdCollision.name,
            }
            setJDCollision(null)
            setJDChoice(next)
            void submit(undefined, next)
          }}
          onUpdateMandate={async () => {
            // Update the existing JD's mandate to the new one, then continue
            // submit using its id. Title stays the same; only the mandate
            // changes, so no further name collision is possible.
            const newMandate = jdChoice.mandate.trim()
            setJDCollision(null)
            setSubmitting(true)
            try {
              const resp = await wsRequest<{
                agent_job_description?: { id: string }
                error?: string
              }>(
                'update_agent_job_description',
                {
                  id: jdCollision.id,
                  title: jdCollision.name,
                  mandate: newMandate,
                  priority: 5,
                },
                'agent_job_description_updated',
                {
                  kind: 'agent_job_description',
                  id: jdCollision.id,
                  action: 'update',
                },
              )
              if (resp.error || !resp.agent_job_description?.id) {
                setError(resp.error ?? 'failed to update job description')
                setSubmitting(false)
                return
              }
              const next: JDChoice = {
                mode: 'existing',
                id: jdCollision.id,
                title: jdCollision.name,
              }
              setJDChoice(next)
              void submit(undefined, next)
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err))
              setSubmitting(false)
            }
          }}
          onCreateWithNewName={(newTitle) => {
            const next: JDChoice = {
              mode: 'new',
              title: newTitle,
              mandate: jdChoice.mode === 'new' ? jdChoice.mandate : '',
            }
            setJDCollision(null)
            setJDChoice(next)
            void submit(undefined, next)
          }}
        />
      )}

      {collision && (
        <NameCollisionSheet
          collision={collision}
          attempted={name.trim()}
          onCancel={() => {
            setCollision(null)
            setSubmitting(false)
          }}
          onPickNewName={() => {
            setCollision(null)
            setSubmitting(false)
            setStep('name')
          }}
          onRetireOld={() => {
            setCollision(null)
            void submit({ remove_conflicting: true })
          }}
          onRenameOld={(newOldName) => {
            setCollision(null)
            void submit({ rename_conflicting_to: newOldName })
          }}
        />
      )}
    </div>
  )
}

function titleFor(step: Step): string {
  switch (step) {
    case 'name':
      return 'Name'
    case 'jd':
      return 'Job Description'
    case 'jd_pick':
      return 'Existing Job Descriptions'
    case 'host':
      return 'Host'
    case 'harness':
      return 'Harness'
    case 'model':
      return 'Model'
    case 'submit':
      return 'Review'
  }
}

function jdTitleOf(j: JDChoice): string {
  return j.mode === 'new' ? j.title : j.title
}

function defaultHostID(hosts: Host[]): string {
  const online = hosts.find((h) => h.online)
  if (online) return online.host_id
  return hosts[0]?.host_id ?? ''
}

function defaultWDPath(
  positionName: string,
  agentHomePath: string | undefined,
  orgSlug: string | null,
): string {
  const snake = positionName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!snake) return ''
  // agent_home_path is the host's hearth_agents root (e.g.
  // /home/alice/hearth_agents). The org slug — snapshotted from the org's
  // display name at create time and immutable thereafter — namespaces
  // two orgs on the same host so their default paths can't collide.
  const base =
    agentHomePath && agentHomePath.trim() !== ''
      ? agentHomePath.replace(/\/+$/, '')
      : '~/hearth_agents'
  const orgSegment = orgSlug && orgSlug !== '' ? orgSlug : 'unknown_org'
  return `${base}/${orgSegment}/${snake}`
}

// ---- Step components ----------------------------------------------------

function NameStep({
  name,
  placeholder,
  onChange,
  onNext,
  existingNames,
}: {
  name: string
  placeholder: string
  onChange: (v: string) => void
  onNext: () => void
  existingNames: Set<string>
}) {
  const collides = name.trim() !== '' && existingNames.has(name.trim())
  return (
    <div className="wizard__step">
      <div className="wizard__hint">Give this agent a short, memorable name.</div>
      <input
        className="wizard__input"
        autoFocus
        value={name}
        placeholder={`e.g. ${placeholder}`}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onNext()
        }}
      />
      {collides && (
        <div className="wizard__warning">
          Another active agent already uses this name. You can keep going —
          you'll be asked how to handle the collision when you create the
          agent.
        </div>
      )}
      <button
        className="wizard__primary"
        disabled={!name.trim()}
        onClick={onNext}
      >
        Next
      </button>
    </div>
  )
}

function JDStep({
  choice,
  onChange,
  onPickExisting,
  onNext,
  existingTitles,
  titlePlaceholder,
}: {
  choice: JDChoice
  onChange: (c: JDChoice) => void
  onPickExisting: () => void
  onNext: () => void
  existingTitles: Set<string>
  titlePlaceholder: string
}) {
  const collides =
    choice.mode === 'new' &&
    choice.title.trim() !== '' &&
    existingTitles.has(choice.title.trim())
  if (choice.mode === 'existing') {
    return (
      <div className="wizard__step">
        <div className="wizard__hint">
          Using an existing job description:
        </div>
        <div className="wizard__review">
          <dt>Title</dt>
          <dd>{choice.title}</dd>
        </div>
        <button
          className="wizard__secondary"
          onClick={() => onChange({ mode: 'new', title: '', mandate: '' })}
        >
          Create new instead
        </button>
        <button className="wizard__primary" onClick={onNext}>
          Next
        </button>
      </div>
    )
  }
  return (
    <div className="wizard__step">
      <div className="wizard__hint">
        Describe the role this agent will fill.
      </div>
      <label className="wizard__field-label">Title</label>
      <input
        className="wizard__input"
        autoFocus
        value={choice.title}
        placeholder={`e.g. ${titlePlaceholder}`}
        onChange={(e) => onChange({ ...choice, title: e.target.value })}
      />
      {collides && (
        <div className="wizard__warning">
          A job description with this title already exists. If the mandate
          you enter matches it, we'll just reuse it; otherwise you'll get
          to pick how to handle it at create time.
        </div>
      )}
      <label className="wizard__field-label">Mandate (optional)</label>
      <textarea
        className="wizard__textarea"
        value={choice.mandate}
        placeholder="What is this role responsible for?"
        onChange={(e) => onChange({ ...choice, mandate: e.target.value })}
      />
      <button
        className="wizard__primary"
        disabled={!choice.title.trim()}
        onClick={onNext}
      >
        Next
      </button>
      <button className="wizard__secondary" onClick={onPickExisting}>
        Use an existing job description
      </button>
    </div>
  )
}

function JDPickStep({
  jobDescriptions,
  onPick,
}: {
  jobDescriptions: JobDescription[] | null
  onPick: (jd: JobDescription) => void
}) {
  if (jobDescriptions === null) {
    return <LoadingStep label="Loading job descriptions…" />
  }
  if (jobDescriptions.length === 0) {
    return (
      <EmptyStep label="No job descriptions yet. Go back and create one." />
    )
  }
  return (
    <div className="wizard__step">
      <div className="wizard__hint">Pick an existing job description.</div>
      <ul className="wizard__list">
        {jobDescriptions.map((j) => (
          <li key={j.id}>
            <button className="wizard__row" onClick={() => onPick(j)}>
              <span>{j.title}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function HostStep({
  hosts,
  selectedID,
  onSelect,
}: {
  hosts: Host[]
  selectedID: string
  onSelect: (id: string) => void
}) {
  if (hosts.length === 0) {
    return (
      <div className="wizard__step">
        <div className="wizard__empty">
          No hosts enrolled. Run <code>hearth start</code> on a computer.
        </div>
      </div>
    )
  }
  return (
    <div className="wizard__step">
      <div className="wizard__hint">
        Which host computer should this agent run on?
      </div>
      <ul className="wizard__list">
        {hosts.map((h) => (
          <li key={h.host_id}>
            <button
              className={`wizard__row${h.host_id === selectedID ? ' wizard__row--selected' : ''}`}
              onClick={() => onSelect(h.host_id)}
            >
              <span>{h.hostname || h.host_id}</span>
              <span
                className="wizard__dot"
                style={{
                  background: h.online
                    ? 'var(--success)'
                    : 'var(--label-tertiary)',
                }}
                aria-label={h.online ? 'online' : 'offline'}
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function HarnessStep({
  harnesses,
  availableIDs,
  hostname,
  selectedID,
  onSelect,
}: {
  harnesses: Harness[] | null
  availableIDs: Set<number>
  hostname: string
  selectedID: number | null
  onSelect: (id: number) => void
}) {
  const [explainID, setExplainID] = useState<number | null>(null)

  if (harnesses === null) {
    return <LoadingStep label="Loading harnesses…" />
  }
  if (harnesses.length === 0) {
    return <EmptyStep label="No harnesses configured." />
  }
  const explainHarness =
    explainID !== null ? harnesses.find((h) => h.id === explainID) : null
  return (
    <div className="wizard__step">
      <div className="wizard__hint">Which agent runtime?</div>
      <ul className="wizard__list">
        {harnesses.map((h) => {
          const available = availableIDs.has(h.id)
          const selected = h.id === selectedID && available
          return (
            <li key={h.id}>
              <div className="wizard__row-wrap">
                <button
                  className={`wizard__row${selected ? ' wizard__row--selected' : ''}${available ? '' : ' wizard__row--unavailable'}`}
                  onClick={() => available && onSelect(h.id)}
                  disabled={!available}
                  aria-disabled={!available}
                >
                  <span>{h.name}</span>
                </button>
                {!available && (
                  <button
                    className="wizard__row-info"
                    aria-label={`Why is ${h.name} unavailable?`}
                    onClick={() => setExplainID(h.id)}
                  >
                    ?
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      {explainHarness && (
        <div
          className="wizard__collision"
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) setExplainID(null)
          }}
        >
          <div className="wizard__collision-sheet">
            <div className="wizard__collision-title">
              {explainHarness.name} unavailable on {hostname}
            </div>
            <div className="wizard__collision-body">
              The {explainHarness.name} CLI wasn't detected on the PATH of
              the daemon running on {hostname}. Install it on that machine
              and restart hearth there, or pick a different host.
            </div>
            <button
              className="wizard__collision-item wizard__collision-item--cancel"
              onClick={() => setExplainID(null)}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ModelStep({
  models,
  selectedID,
  onSelect,
}: {
  models: AIBrainModel[] | null
  selectedID: string | null
  onSelect: (id: string) => void
}) {
  if (models === null) {
    return <LoadingStep label="Loading models…" />
  }
  if (models.length === 0) {
    return (
      <EmptyStep label="No models are compatible with the selected harness. Pick a different harness, or add a compatible model first: `hearth hh ai_model create`." />
    )
  }
  return (
    <div className="wizard__step">
      <div className="wizard__hint">Pick an AI brain for this agent.</div>
      <ul className="wizard__list">
        {models.map((m) => (
          <li key={m.id}>
            <button
              className={`wizard__row${m.id === selectedID ? ' wizard__row--selected' : ''}`}
              onClick={() => onSelect(m.id)}
            >
              <span>{m.name}</span>
              {m.provider && (
                <span className="wizard__row-sub">{m.provider}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SubmitStep({
  agentName,
  jd,
  wdPath,
  onChangeWDPath,
  hostname,
  harnessName,
  modelName,
  onSubmit,
  onCancel,
  submitting,
  error,
  existingWDPaths,
  allowDiscovery,
  onToggleDiscovery,
  connections,
  grantConnIDs,
  onToggleConn,
}: {
  agentName: string
  jd: JDChoice
  wdPath: string
  onChangeWDPath: (v: string) => void
  hostname: string
  harnessName: string
  modelName: string
  onSubmit: () => void
  onCancel: () => void
  submitting: boolean
  error: string | null
  existingWDPaths: Set<string>
  allowDiscovery: boolean
  onToggleDiscovery: (v: boolean) => void
  connections: { id: string; display_name: string }[]
  grantConnIDs: string[]
  onToggleConn: (id: string) => void
}) {
  const jdLabel =
    jd.mode === 'existing' ? `${jd.title} (existing)` : jd.title || '—'
  const [editingPath, setEditingPath] = useState(false)
  const [draftPath, setDraftPath] = useState(wdPath)
  const wdCollides = wdPath.trim() !== '' && existingWDPaths.has(wdPath.trim())

  // Keep the draft in sync with the outer value while not actively
  // editing, so a back-and-forward through the flow reseeds it.
  useEffect(() => {
    if (!editingPath) setDraftPath(wdPath)
  }, [wdPath, editingPath])

  function commit() {
    const trimmed = draftPath.trim()
    if (trimmed !== '') onChangeWDPath(trimmed)
    setEditingPath(false)
  }

  return (
    <div className="wizard__step">
      <div className="wizard__hint">Review and create.</div>
      <dl className="wizard__review">
        <dt>Name</dt>
        <dd>{agentName}</dd>
        <dt>Job Description</dt>
        <dd>{jdLabel}</dd>
        <dt>Host</dt>
        <dd>{hostname}</dd>
        <dt>Harness</dt>
        <dd>{harnessName}</dd>
        <dt>Model</dt>
        <dd>{modelName}</dd>
        <dt>Working Directory</dt>
        <dd>
          {editingPath ? (
            <div className="wizard__review-edit">
              <input
                className="wizard__input"
                autoFocus
                value={draftPath}
                onChange={(e) => setDraftPath(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit()
                  if (e.key === 'Escape') {
                    setDraftPath(wdPath)
                    setEditingPath(false)
                  }
                }}
              />
              <button
                type="button"
                className="wizard__review-edit-btn"
                onClick={commit}
                aria-label="Save working directory path"
              >
                ✓
              </button>
            </div>
          ) : (
            <div className="wizard__review-edit">
              <span className="wizard__review-path">{wdPath || '—'}</span>
              <button
                type="button"
                className="wizard__review-edit-btn"
                onClick={() => setEditingPath(true)}
                aria-label="Edit working directory path"
              >
                ✏️
              </button>
            </div>
          )}
          {wdCollides && (
            <div className="wizard__warning">
              This path is already a working directory on {hostname}. If the
              position pinned to it has no live agents, we'll take the slot
              automatically; otherwise you'll need to pick a different path.
            </div>
          )}
        </dd>
      </dl>
      <label className="wizard__toggle-row">
        <input
          type="checkbox"
          checked={allowDiscovery}
          onChange={(e) => onToggleDiscovery(e.target.checked)}
        />
        <span>
          Let this agent discover other things in the household — your other
          agents, screens, speakers, and connected services. Leave off to keep it
          focused on what you give it directly.
        </span>
      </label>
      {connections.length > 0 && (
        <div className="wizard__grant-block">
          <div className="wizard__grant-label">
            Give this agent access to (optional):
          </div>
          {connections.map((c) => (
            <label key={c.id} className="wizard__toggle-row">
              <input
                type="checkbox"
                checked={grantConnIDs.includes(c.id)}
                onChange={() => onToggleConn(c.id)}
              />
              <span>{c.display_name || c.id}</span>
            </label>
          ))}
          <div className="wizard__grant-hint">
            Granting now means it won't have to stop and ask for approval the
            first time it needs one.
          </div>
        </div>
      )}
      {error && <div className="wizard__error">{error}</div>}
      <button
        className="wizard__primary"
        onClick={onSubmit}
        disabled={submitting || !wdPath.trim()}
      >
        {submitting ? 'Creating…' : 'Create Agent'}
      </button>
      <button
        className="wizard__secondary"
        onClick={onCancel}
        disabled={submitting}
      >
        Cancel
      </button>
    </div>
  )
}

function LoadingStep({ label }: { label: string }) {
  return (
    <div className="wizard__step">
      <div className="wizard__empty">{label}</div>
    </div>
  )
}

function EmptyStep({ label }: { label: string }) {
  return (
    <div className="wizard__step">
      <div className="wizard__empty">{label}</div>
    </div>
  )
}

// ---- Name-collision sheet ----------------------------------------------

function NameCollisionSheet({
  collision,
  attempted,
  onCancel,
  onPickNewName,
  onRetireOld,
  onRenameOld,
}: {
  collision: NameCollision
  attempted: string
  onCancel: () => void
  onPickNewName: () => void
  onRetireOld: () => void
  onRenameOld: (newOldName: string) => void
}) {
  const [mode, setMode] = useState<'menu' | 'rename'>('menu')
  const [renameTo, setRenameTo] = useState(
    collision.suggested_rename || `${collision.name} (old)`,
  )

  return (
    <div className="wizard__collision" role="dialog">
      <div className="wizard__collision-sheet">
        <div className="wizard__collision-title">Name already in use</div>
        <div className="wizard__collision-body">
          "{attempted}" is the name of another agent in this household.
        </div>
        {mode === 'menu' && (
          <>
            <button className="wizard__collision-item" onClick={onPickNewName}>
              Use a different name
            </button>
            <button className="wizard__collision-item" onClick={onRetireOld}>
              Retire the existing agent and continue
            </button>
            <button
              className="wizard__collision-item"
              onClick={() => setMode('rename')}
            >
              Rename the existing agent and continue
            </button>
            <button
              className="wizard__collision-item wizard__collision-item--cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          </>
        )}
        {mode === 'rename' && (
          <>
            <label className="wizard__field-label">New name for existing agent</label>
            <input
              className="wizard__input"
              autoFocus
              value={renameTo}
              onChange={(e) => setRenameTo(e.target.value)}
            />
            <button
              className="wizard__primary"
              disabled={!renameTo.trim()}
              onClick={() => onRenameOld(renameTo.trim())}
            >
              Rename and Continue
            </button>
            <button
              className="wizard__collision-item wizard__collision-item--cancel"
              onClick={() => setMode('menu')}
            >
              Back
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ---- JD-collision sheet ------------------------------------------------

function JDCollisionSheet({
  collision,
  attemptedTitle,
  attemptedMandate,
  onCancel,
  onUseExisting,
  onUpdateMandate,
  onCreateWithNewName,
}: {
  collision: NameCollision
  attemptedTitle: string
  attemptedMandate: string
  onCancel: () => void
  onUseExisting: () => void
  onUpdateMandate: () => void
  onCreateWithNewName: (newTitle: string) => void
}) {
  const [mode, setMode] = useState<'menu' | 'rename'>('menu')
  const [newTitle, setNewTitle] = useState(
    collision.suggested_new_name || `${attemptedTitle} (2)`,
  )

  const existingMandate = collision.existing_mandate?.trim() ?? ''
  const newMandate = attemptedMandate
  const activeCount = collision.active_agent_count ?? 0
  const sameMandate = existingMandate === newMandate

  return (
    <div className="wizard__collision" role="dialog">
      <div className="wizard__collision-sheet wizard__collision-sheet--wide">
        <div className="wizard__collision-title">
          A job description with this title already exists
        </div>
        <div className="wizard__collision-body">
          "{attemptedTitle}" is already a job in this household. Pick how you'd
          like to proceed.
        </div>

        <div className="wizard__jd-mandates">
          <div className="wizard__jd-mandate">
            <div className="wizard__jd-mandate-label">Existing mandate</div>
            <div className="wizard__jd-mandate-text">
              {existingMandate || <em>(none)</em>}
            </div>
          </div>
          <div className="wizard__jd-mandate">
            <div className="wizard__jd-mandate-label">Your new mandate</div>
            <div className="wizard__jd-mandate-text">
              {newMandate || <em>(none)</em>}
            </div>
          </div>
        </div>

        {mode === 'menu' && (
          <>
            <button
              className="wizard__collision-item"
              onClick={onUseExisting}
            >
              Use the existing job description
              {sameMandate && existingMandate !== '' && (
                <span className="wizard__collision-hint">
                  {' '}
                  — mandates already match
                </span>
              )}
            </button>
            <button
              className="wizard__collision-item"
              onClick={onUpdateMandate}
              disabled={sameMandate}
            >
              Update the existing job with your new mandate
              {activeCount > 0 && (
                <span className="wizard__collision-hint">
                  {' '}
                  — affects {activeCount} active agent
                  {activeCount === 1 ? '' : 's'} using this job
                </span>
              )}
            </button>
            <button
              className="wizard__collision-item"
              onClick={() => setMode('rename')}
            >
              Create a new job description with a different title
            </button>
            <button
              className="wizard__collision-item wizard__collision-item--cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          </>
        )}
        {mode === 'rename' && (
          <>
            <label className="wizard__field-label">
              Title for the new job description
            </label>
            <input
              className="wizard__input"
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <button
              className="wizard__primary"
              disabled={!newTitle.trim() || newTitle.trim() === attemptedTitle}
              onClick={() => onCreateWithNewName(newTitle.trim())}
            >
              Create and Continue
            </button>
            <button
              className="wizard__collision-item wizard__collision-item--cancel"
              onClick={() => setMode('menu')}
            >
              Back
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// A shuffling of butlers / chiefs of staff from popular culture. One is
// picked at random each time the wizard opens so the name step feels
// alive instead of assigning a canonical default.
const BUTLER_NAMES = [
  'Alfred',
  'Jeeves',
  'Carson',
  'Geoffrey',
  'Niles',
  'Lurch',
  'Hudson',
  'Belvedere',
]

function randomButlerName(): string {
  return BUTLER_NAMES[Math.floor(Math.random() * BUTLER_NAMES.length)]
}

// Household-help roles used as the rotating placeholder in the JD title
// field. Same idea as BUTLER_NAMES — gives the new-JD step some warmth
// instead of a single canned example.
const HOUSEHOLD_ROLES = [
  'Head Gardener',
  'Chef de Cuisine',
  'Pastry Chef',
  'Sommelier',
  'Chauffeur',
  'Nanny',
  'Governess',
  'Math Tutor',
  'Valet',
  'Housekeeper',
  'Majordomo',
  'Steward',
  'Chamberlain',
  'Footman',
  'Gamekeeper',
  'Falconer',
  'Coachman',
  'Scullery Maid',
  'Florist',
  'Dog Walker',
  'Personal Secretary',
  'Bookkeeper',
  'Piano Tuner',
  'Night Watchman',
  'Au Pair',
  'House DJ',
  'Archivist',
  'Grocery Shopper',
  'Muse',
]

function randomHouseholdRole(): string {
  return HOUSEHOLD_ROLES[Math.floor(Math.random() * HOUSEHOLD_ROLES.length)]
}
